// @ts-strict-ignore
import { useEffect, useState, type ReactNode } from "react";

import { Spacer, Columns, Column, Arrange } from "@aviary";
import type { AvailabilityPillStatus } from "@aviary/molecules/ProductImage/AvailabilityPill";
import { ProductImageGallery } from "@aviary/molecules/ProductImageGallery";
import { useGlobalConfig } from "@clinic/shared/hooks/useGlobalConfig/useGlobalConfig";
import { useCatalogProductQuery } from "@emerson/data/queries/CatalogProductQuery/CatalogProduct.query";
import { useProductTaxonomiesQuery } from "@emerson/data/queries/ProductTaxonomiesQuery/ProductTaxonomies.query";
import { useWholesaleCartQuery } from "@shared/data/queries/WholesaleCartQuery/WholesaleCart.query";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useCertifications } from "@shared/hooks/useCertifications/useCertifications";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";

import { Breadcrumb } from "../Breadcrumb/Breadcrumb";

import { AdditionalProductDetails } from "./AdditionalProductDetails/AdditionalProductDetails";
import { ProductActions } from "./ProductActions/ProductActions";
import { ProductNameAndSku } from "./ProductNameAndSku/ProductNameAndSku";
import { SkeletonSingleProductDetails } from "./SkeletonSingleProductDetails";
import { getBreadcrumbLinkItems } from "./helper";

import * as styles from "./SingleProductDetails.styles";

interface Props {
  productId: string;
  isQuickView?: boolean;
  children?: ReactNode;
  handleSimilarProductsClick?: () => void;
}

const QuickViewSingleProductDetails = ({
  productId,
  isQuickView = true,
  children,
  handleSimilarProductsClick,
}: Props) => {
  const { desktop, tablet, phoneLarge } = useBreakpoints();
  const { freeShippingThreshold } = useGlobalConfig();
  const { isUnauthenticated } = useSharedGlobalConfig();
  const { isCertified, loading: certLoading } = useCertifications(isUnauthenticated);

  const setupIncomplete = !isUnauthenticated && !certLoading && !isCertified;

  const colWidth = phoneLarge.lessThan ? 12 : undefined;
  const imageColumnWidth = desktop.greaterThan ? 7 : colWidth;
  const productInfoColWidth = desktop.greaterThan ? 5 : colWidth;
  const isFullWidth = tablet.lessThan || tablet.active;

  const { loading, data } = useCatalogProductQuery({
    variables: {
      productId,
      filters: {
        size: "LARGE",
      },
      isUnauthenticated,
    },
  });

  const { loading: taxonomiesLoading, data: productTaxonomiesData } = useProductTaxonomiesQuery({
    variables: {
      productId,
    },
    skip: isQuickView,
  });

  const { data: wholesaleCartData } = useWholesaleCartQuery({
    fetchPolicy: "cache-and-network",
    skip: isUnauthenticated,
  });

  const { itemTotal: subtotal } = wholesaleCartData?.currentOffice?.wholesaleCart || {
    itemTotal: 0,
  };

  const freeShippingAmountLeft = freeShippingThreshold - subtotal;

  const product = data?.viewer?.product;
  const availableOrMasterVariant = product?.availableOrMasterVariant;
  const taxonomies = productTaxonomiesData?.viewer?.product?.taxonomies;

  const [currentVariantId, setCurrentVariantId] = useState(availableOrMasterVariant?.id);

  useEffect(() => {
    if (loading) return;
    setCurrentVariantId(availableOrMasterVariant?.id);
  }, [loading, availableOrMasterVariant]);

  if (loading) {
    return <SkeletonSingleProductDetails />;
  }

  if (!loading && !product) return null;

  const productName = product?.name;
  const skuParts = availableOrMasterVariant?.sku.split("-");
  const vendorItemCode = skuParts?.[1];
  const productMainImage = availableOrMasterVariant?.imagesNew?.[0];
  const variants = data?.viewer?.product?.variants;

  const renderAdditionalInfo = () => (
    <AdditionalProductDetails
      product={product}
      variant={availableOrMasterVariant}
      isQuickView={isQuickView}
    />
  );

  const infoContainerStyle = [
    styles.infoContainer.base,
    !isQuickView && setupIncomplete && styles.infoContainer.withSetupBar,
    isQuickView && styles.infoContainer.withModal,
  ];

  const selectedVariant = variants?.find(({ id }) => id === currentVariantId);
  const images = selectedVariant?.imagesNew.flat() || [];

  const renderBreadcrumbs = () =>
    !taxonomiesLoading &&
    taxonomies && <Breadcrumb breadcrumbLinkItems={getBreadcrumbLinkItems(taxonomies[0])} />;

  return (
    <div css={styles.wrapper}>
      {renderBreadcrumbs()}
      <Spacer height={phoneLarge.lessThan ? "one" : "oneHalf"} />
      <Columns isMarginless css={styles.container}>
        <Column isPaddingless columnWidth={imageColumnWidth}>
          <ProductImageGallery
            key={productId}
            name={productName}
            mainImage={productMainImage}
            images={images}
            qualityPartner={product.eqpLevel}
            availabilityStatus={selectedVariant?.availabilityStatus as AvailabilityPillStatus}
            showZoom={!isQuickView}
          />
          {!!tablet.greaterThan && (
            <>
              <Spacer height="doubleHalf" />
              {renderAdditionalInfo()}
            </>
          )}
        </Column>
        <Column isPaddingless columnWidth={productInfoColWidth} css={infoContainerStyle}>
          <Arrange spacing="large" isFullWidth isVertical>
            <ProductNameAndSku
              productName={productName}
              sku={selectedVariant?.emersonVariantId}
              vendorItemCode={vendorItemCode}
              brand={product?.brand}
            />
            <ProductActions
              freeShippingAmountLeft={freeShippingAmountLeft}
              product={product}
              handleSimilarProductsClick={handleSimilarProductsClick}
              setCurrentVariantId={setCurrentVariantId}
              currentVariantId={currentVariantId}
              isQuickView={isQuickView}
            />
          </Arrange>
        </Column>
      </Columns>
      {!!isFullWidth && (
        <>
          <Spacer height="oneHalf" />
          <Columns isMarginless>
            <Column isPaddingless columnWidth={12}>
              {renderAdditionalInfo()}
            </Column>
          </Columns>
        </>
      )}
      {children}
    </div>
  );
};

export { QuickViewSingleProductDetails };
